import {ChecksStatusBadge} from '@github-ui/commit-checks-status'
import type {VariantType} from '@github-ui/list-view/ListViewVariantContext'
import {Suspense} from 'react'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import type {
  CheckRunStatusFromPullRequest$data,
  CheckRunStatusFromPullRequest$key,
} from './__generated__/CheckRunStatusFromPullRequest.graphql'
import type {IssueRowSecondaryQuery} from './__generated__/IssueRowSecondaryQuery.graphql'
import styles from './CheckRunStatus.module.css'
import {IssuesIndexSecondaryGraphqlQuery} from './IssueRow'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const CheckRunStatusFromPullRequestFragment = graphql`
  fragment CheckRunStatusFromPullRequest on PullRequest {
    statusCheckRollup {
      state
      contexts {
        checkRunCount
        checkRunCountsByState {
          count
          state
        }
      }
    }
  }
`
function CheckRunStatus({
  statusCheckRollup,
  variant,
}: {
  statusCheckRollup: NonNullable<CheckRunStatusFromPullRequest$data['statusCheckRollup']>
  variant?: VariantType
}) {
  if (!statusCheckRollup) return null
  const checkRunTotalCount = statusCheckRollup.contexts?.checkRunCount
  const checkRunSuccessCount = statusCheckRollup.contexts?.checkRunCountsByState?.find(
    runCountByState => runCountByState.state === 'SUCCESS',
  )?.count
  const checkRunText = `${checkRunSuccessCount}/${checkRunTotalCount}`
  const statusRollup = statusCheckRollup.state.toLowerCase() || ''
  return (
    <span className={styles.checkRunStatusContainer}>
      {' '}
      · <ChecksStatusBadge disablePopover statusRollup={statusRollup} />
      <span>{variant === 'default' && checkRunText}</span>
    </span>
  )
}

export function LazyCheckRunStatus({
  id,
  primaryQueryRef,
  secondaryQueryRef,
  variant,
}: {
  id: string
  primaryQueryRef?: CheckRunStatusFromPullRequest$key
  secondaryQueryRef?: PreloadedQuery<IssueRowSecondaryQuery> | null
  variant: VariantType
}) {
  if (!secondaryQueryRef && !primaryQueryRef) return null

  if (primaryQueryRef) {
    return <LazyCheckRunStatusFromPRInternal dataKey={primaryQueryRef} variant={variant} />
  }

  if (!secondaryQueryRef) return null

  return (
    <Suspense fallback={null}>
      <LazyCheckRunStatusFetched id={id} secondaryQueryRef={secondaryQueryRef} variant={variant} />
    </Suspense>
  )
}

function LazyCheckRunStatusFetched({
  id,
  secondaryQueryRef,
  variant,
}: {
  id: string
  secondaryQueryRef: PreloadedQuery<IssueRowSecondaryQuery>
  variant: VariantType
}) {
  const {nodes} = usePreloadedQuery<IssueRowSecondaryQuery>(IssuesIndexSecondaryGraphqlQuery, secondaryQueryRef)
  const prNode = nodes?.find(node => node?.id === id)

  if (!prNode) return null

  return <LazyCheckRunStatusFromPRInternal dataKey={prNode} variant={variant} />
}

function LazyCheckRunStatusFromPRInternal({
  dataKey,
  variant,
}: {
  dataKey: CheckRunStatusFromPullRequest$key
  variant: VariantType
}) {
  const data = useFragment(CheckRunStatusFromPullRequestFragment, dataKey)

  if (!data?.statusCheckRollup) return null

  return <CheckRunStatus statusCheckRollup={data.statusCheckRollup} variant={variant} />
}
